import { useCallback, useMemo, useState } from 'react';
import axios from 'src/utils/axios';
import type {
  CaseSummary,
  SavePromptOffer,
} from 'src/components/SupportCase/types';

interface PageContext {
  certificationId: number | null;
  propertySlug: string | null;
  pagePath: string;
}

/**
 * What the user is looking at, as far as the URL can tell (ENG-4137).
 *
 * Captured when a message is sent, not when the component mounts, because the point of
 * a persistent composer is that the user can move around first and ask about the page
 * they ended up on. Only ids go to the server; the page path is included so the agent
 * can tell which tab the user is on.
 */
export const readPageContext = (): PageContext => {
  const pagePath = window.location.pathname;
  const segments = pagePath.split('/').filter(Boolean);
  // workbench/<property-slug>/<certification-id>/…
  //
  // ENG-4251 (PA-01): a property page carries a **slug**, and the certification
  // segment is only an id when it is all digits. The previous version looked only at
  // segment 2 and sent propertyId: null always, so a property page sent no context at
  // all. Reserved workbench routes are not property slugs.
  const RESERVED = new Set([
    'support',
    'team-renters',
    'my-work',
    'my-work-board',
    'workgroups',
    'property',
  ]);
  let certificationId: number | null = null;
  let propertySlug: string | null = null;
  if (segments[0] === 'workbench' && segments.length >= 2) {
    const candidate = segments[1];
    if (candidate && !RESERVED.has(candidate)) {
      propertySlug = candidate;
    }
    const certSegment = segments[2];
    if (certSegment && /^\d+$/.test(certSegment)) {
      certificationId = Number(certSegment);
    }
  }
  return { certificationId, propertySlug, pagePath };
};

/**
 * One idempotency key for one message (ENG-4220).
 *
 * The composer mints its own because it must keep the *same* id across retries; a
 * chip run is a single attempt, so it just needs a fresh id. Exported so the three
 * places that send without a composer do not grow three spellings of `randomUUID`.
 */
export const newClientMsgId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `cm-${Date.now()}-${Math.random().toString(16).slice(2)}`;

/** The failure sentence both composers show, so the two surfaces cannot drift. */
export const CREATE_CASE_ERROR =
  'Could not send that message. Check your connection and try again.';

/**
 * Context refusals the server names, and the only ones worth retrying (PA-23).
 *
 * A 400 caused by empty text or a bad attachment id must not be retried: dropping
 * the page context would not fix it and would silently discard the user's text. These
 * three mean "the page you were on is not one I can resolve" — a stale route, not a
 * bad message — so the second attempt sends the message alone.
 */
export const CONTEXT_REFUSALS = new Set([
  'invalid_context',
  'out_of_scope',
  'not_found',
]);

export interface StartCaseResult {
  /** Send one first message, creating the case. False means "show the error". */
  send: (
    message: string,
    clientMsgId: string,
    attachmentIds: number[]
  ) => Promise<boolean>;
  /** The case the first message created, once it exists. */
  created: CaseSummary | null;
  /**
   * The server's offer to save this question as a prompt (ENG-4220).
   *
   * Present only on the third identical question inside thirty days, only when
   * nothing is saved with that key and the user is under the cap — the server owns
   * that judgement, so the client never has to decide whether to nag.
   */
  suggested: SavePromptOffer | null;
  /** True while the create is in flight, so Send can be disabled. */
  creating: boolean;
  error: string | null;
  /** Clear a previous failure before retrying. */
  reset: () => void;
}

/**
 * Create a support case from a first message (ENG-4211).
 *
 * Extracted verbatim from `SupportCasePanel`, which owned the only implementation.
 * The hub's Assistant tab needs the same call — the same page context, the same
 * idempotency key, the same attachment ids — and a second copy would drift: the
 * attachment bug in ENG-4185 (a two-argument handler silently satisfying a
 * three-argument callback type) is exactly what a copy costs.
 *
 * The caller decides what to do with `created`: the panel keeps the conversation
 * inline, the hub navigates to the case's own URL.
 */
const useStartCase = (): StartCaseResult => {
  const [created, setCreated] = useState<CaseSummary | null>(null);
  const [suggested, setSuggested] = useState<SavePromptOffer | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mount-time context, the fallback when the live read has no certification in the
  // URL (the panel opens from a property page as well as a certification page).
  const context = useMemo(() => readPageContext(), []);

  const reset = useCallback(() => setError(null), []);

  const send = useCallback(
    async (
      message: string,
      clientMsgId: string,
      attachmentIds: number[]
    ): Promise<boolean> => {
      setCreating(true);
      setError(null);
      // A new question is a new offer (or none); the previous case's offer must not
      // survive into the next send.
      setSuggested(null);
      try {
        const live = readPageContext();
        const certificationId = live.certificationId ?? context.certificationId;
        const propertySlug = live.propertySlug ?? context.propertySlug;

        const create = (withContext: boolean): ReturnType<typeof axios.post> =>
          axios.post('/support/cases/', {
            message,
            client_msg_id: clientMsgId,
            ...(withContext
              ? {
                  certification_id: certificationId,
                  property_slug: propertySlug,
                }
              : {}),
            page_path: live.pagePath,
            page_context: { page_path: live.pagePath },
            // The first message of a new case can carry files too. Omitting this was a
            // real bug: the file uploaded, the chip appeared, Send worked, and the turn
            // arrived with no attachments — so the assistant answered "there is nothing
            // attached".
            attachment_ids: attachmentIds,
          });

        let response;
        try {
          response = await create(true);
        } catch (err) {
          // A route context the server refuses is a stale page, not a bad message:
          // retry once without it, with the same idempotency key, so the user's first
          // message is never lost to a slug they cannot see.
          //
          // PA-23 — keyed on the server's own vocabulary, not on a 4xx class. Retrying
          // every 4xx would retry a 400 caused by empty text.
          const code = (err as { response?: { data?: { error?: string } } })
            ?.response?.data?.error;
          if (!code || !CONTEXT_REFUSALS.has(code)) {
            throw err;
          }
          response = await create(false);
        }
        const summary = response.data?.case as CaseSummary | undefined;
        if (summary) {
          setCreated(summary);
        }
        const offer = response.data?.suggest_save_prompt as
          | SavePromptOffer
          | undefined;
        if (offer?.body) {
          setSuggested(offer);
        }
        return true;
      } catch {
        setError(CREATE_CASE_ERROR);
        return false;
      } finally {
        setCreating(false);
      }
    },
    [context.certificationId, context.propertySlug]
  );

  return { send, created, suggested, creating, error, reset };
};

export default useStartCase;
